import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase-config';
import { Loader2 } from 'lucide-react';

interface CustomerAdminRouteProps {
  children: ReactNode;
}

export default function CustomerAdminRoute({ children }: CustomerAdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      console.log('🔐 CustomerAdminRoute: Checking access for slug:', slug, 'user:', user?.email);

      if (!user || !slug) {
        console.log('❌ CustomerAdminRoute: No user or slug, denying access');
        setIsAuthorized(false);
        setChecking(false);
        return;
      }

      try {
        // First check if user is a super admin (they can access all customers)
        // Note: This will throw permission-denied if user is not a super admin, which is expected
        let isSuperAdmin = false;
        try {
          const adminDoc = await getDoc(doc(db, 'admins', user.uid));
          isSuperAdmin = adminDoc.exists();
          if (isSuperAdmin) {
            console.log('✅ CustomerAdminRoute: User is super admin, granting access');
            setIsAuthorized(true);
            setChecking(false);
            return;
          }
        } catch (superAdminError: any) {
          // Permission denied is expected for non-super-admins, continue to customer admin check
          if (superAdminError.code !== 'permission-denied') {
            console.error('❌ CustomerAdminRoute: Unexpected error checking super admin:', superAdminError);
          }
          console.log('ℹ️ CustomerAdminRoute: User is not super admin, checking customer admin access');
        }

        // Find customer by slug
        const customersRef = collection(db, 'customers');
        const q = query(customersRef, where('slug', '==', slug));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          // Customer doesn't exist
          console.log('❌ CustomerAdminRoute: Customer with slug not found:', slug);
          setIsAuthorized(false);
          setChecking(false);
          return;
        }

        const customerId = snapshot.docs[0].id;
        console.log('📋 CustomerAdminRoute: Found customer ID:', customerId);

        // Check if user is a customer admin for this customer
        const customerAdminDoc = await getDoc(
          doc(db, 'customers', customerId, 'admins', user.uid)
        );

        const isAdmin = customerAdminDoc.exists();
        console.log(
          isAdmin ? '✅ CustomerAdminRoute: User is customer admin, granting access' : '❌ CustomerAdminRoute: User is NOT customer admin, denying access',
          '\nAdmin doc path:',
          `customers/${customerId}/admins/${user.uid}`,
          '\nExists:',
          isAdmin
        );

        setIsAuthorized(isAdmin);
      } catch (error) {
        console.error('❌ CustomerAdminRoute: Error checking customer admin access:', error);
        setIsAuthorized(false);
      } finally {
        setChecking(false);
      }
    };

    if (!authLoading) {
      checkAccess();
    }
  }, [user, authLoading, slug]);

  // Show loading spinner while checking auth and permissions
  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to home if not authenticated or not authorized
  if (!user || !isAuthorized) {
    return <Navigate to="/" replace />;
  }

  // User is authorized, render the protected content
  return <>{children}</>;
}
